import { Navigate, createBrowserRouter } from 'react-router-dom';

import { AuthGuard } from '@/auth/AuthGuard';
import { useAuth } from '@/auth/AuthProvider';
import { MainLayout } from '@/components/Layout/MainLayout';

// Lazy load pages for code splitting
import { lazy } from 'react';

const LoginPage = lazy(() => import('@/pages/Login'));
const DashboardPage = lazy(() => import('@/pages/Dashboard'));
const UsuariosPage = lazy(() => import('@/pages/Usuarios'));
const LotesPage = lazy(() => import('@/pages/Lotes'));
const ReservasVencidasPage = lazy(() => import('@/pages/ReservasVencidas'));
const ReservasPorVencerPage = lazy(() => import('@/pages/ReservasPorVencer'));
const LoteDetallePage = lazy(() => import('@/pages/LoteDetalle'));
const BusquedaCompradorPage = lazy(() => import('@/pages/BusquedaComprador'));
const LotificacionesPage = lazy(() => import('@/pages/Lotificaciones'));
const LotificacionDetailPage = lazy(() => import('@/pages/Lotificaciones/LotificacionDetail'));
const ReportesReservasPage = lazy(() => import('@/pages/Reportes/Reservas'));
const ReportesVentasPage = lazy(() => import('@/pages/Reportes/Ventas'));
const PerfilPage = lazy(() => import('@/pages/Perfil'));
const NotFoundPage = lazy(() => import('@/pages/NotFound'));
const CompradorDetallePage = lazy(() => import('@/pages/CompradorDetalle'));
const CalculadoraPage = lazy(() => import('@/pages/Calculadora'));

const IndexRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'ASISTENTE') {
    return <Navigate to='/lotificaciones' replace />;
  }
  return <Navigate to='/dashboard' replace />;
};

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <MainLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <IndexRedirect />,
      },
      {
        path: 'dashboard',
        element: (
          <AuthGuard allowedRoles={['ADMIN', 'VENDEDOR']}>
            <DashboardPage />
          </AuthGuard>
        ),
      },
      {
        path: 'usuarios',
        element: (
          <AuthGuard allowedRoles={['ADMIN']}>
            <UsuariosPage />
          </AuthGuard>
        ),
      },
      {
        path: 'lotes',
        element: <LotesPage />,
      },
      {
        path: 'lotes/vencidas',
        element: <ReservasVencidasPage />,
      },
      {
        path: 'lotes/por-vencer/:dias',
        element: <ReservasPorVencerPage />,
      },
      {
        path: 'lotes/:id',
        element: <LoteDetallePage />,
      },
      {
        path: 'busqueda',
        element: <BusquedaCompradorPage />,
      },
      {
        path: 'calculadora',
        element: (
          <AuthGuard allowedRoles={['ADMIN', 'VENDEDOR']}>
            <CalculadoraPage />
          </AuthGuard>
        ),
      },
      {
        path: 'lotificaciones',
        element: (
          <AuthGuard allowedRoles={['ADMIN', 'ASISTENTE']}>
            <LotificacionesPage />
          </AuthGuard>
        ),
      },
      {
        path: 'lotificaciones/:id',
        element: (
          <AuthGuard allowedRoles={['ADMIN', 'ASISTENTE']}>
            <LotificacionDetailPage />
          </AuthGuard>
        ),
      },
      {
        path: 'reportes',
        children: [
          {
            path: 'reservas',
            element: (
              <AuthGuard allowedRoles={['ADMIN']}>
                <ReportesReservasPage />
              </AuthGuard>
            ),
          },
          {
            path: 'ventas',
            element: (
              <AuthGuard allowedRoles={['ADMIN']}>
                <ReportesVentasPage />
              </AuthGuard>
            ),
          },
        ],
      },
      {
        path: 'perfil',
        element: <PerfilPage />,
      },
      {
        path: 'compradores/:id',
        element: <CompradorDetallePage />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);